import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FileText, RefreshCw, Crown, TrendingUp, UserPlus, Repeat, AlertTriangle, BarChart3, Calendar, Award, Send, LineChart as LineChartIcon } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import useElementToPdf from '../../utils/useElementToPdf';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import BarberRatingDialog from '../../components/admin/BarberRatingDialog';
import UnlinkedRevenuesWidget from '../../components/UnlinkedRevenuesWidget';

/**
 * Admin-only "Weekly Loyalty Reports" page.
 *  • Live preview of the in-progress Saturday→Friday week.
 *  • History of sealed weekly reports (one per week, generated automatically
 *    every Saturday 09:00 UAE) for week-over-week comparison.
 *  • Each report exports to a real A4 PDF.
 */
const PctChip = ({ value, color }) => (
  <span
    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold border"
    style={{ background: `${color}18`, borderColor: `${color}55`, color }}
  >
    {Number(value || 0).toFixed(1)}%
  </span>
);

const ScoreBadge = ({ score, onClick, clickable }) => {
  const v = Number(score || 0);
  let label = 'يحتاج تحسين';
  let color = '#ef4444';
  if (v >= 75) { label = 'ممتاز'; color = '#10b981'; }
  else if (v >= 50) { label = 'جيد'; color = '#d4af37'; }
  else if (v >= 30) { label = 'متوسط'; color = '#f59e0b'; }
  const Cmp = clickable ? 'button' : 'div';
  return (
    <Cmp
      type={clickable ? 'button' : undefined}
      onClick={clickable ? onClick : undefined}
      title={clickable ? 'اضغط لعرض مقترحات التحسين وتسلسل تقييمك' : undefined}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-bold text-xs ${clickable ? 'cursor-pointer hover:opacity-80 transition-opacity ring-0 hover:ring-2' : ''}`}
      style={{ background: `${color}22`, color, border: `1px solid ${color}55` }}
    >
      <Award className="w-3.5 h-3.5" /> {v} · {label}
    </Cmp>
  );
};

const ReportSheet = React.forwardRef(({ report, prevReport }, ref) => {
  if (!report) return null;
  const t = report.totals || {};
  const pt = prevReport?.totals || null;

  const diff = (cur, prev) => {
    if (!prev) return null;
    const d = (cur || 0) - (prev || 0);
    if (Math.abs(d) < 0.05) return null;
    return d;
  };

  return (
    <div
      ref={ref}
      dir="rtl"
      style={{
        width: '210mm',
        minHeight: '295mm',
        padding: '12mm',
        background: '#fff',
        color: '#111',
        fontFamily: 'Cairo, Tajawal, Arial, sans-serif',
        boxSizing: 'border-box',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '3px double #d4af37', paddingBottom: 8, marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800 }}>KE Gents Saloon</div>
          <div style={{ fontSize: 11, color: '#666' }}>التقرير الأسبوعي لبرنامج الولاء</div>
        </div>
        <div style={{ textAlign: 'left' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#d4af37' }}>{report.week_label}</div>
          <div style={{ fontSize: 10, color: '#666' }}>طُبع: {new Date().toLocaleDateString('ar-AE')}</div>
        </div>
      </div>

      {/* Totals row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 14 }}>
        {[
          { label: 'إجمالي الزيارات', value: t.visits_count, prev: pt?.visits_count, suffix: '' },
          { label: 'إجمالي الإيرادات', value: t.revenue_total, prev: pt?.revenue_total, suffix: ' د.إ' },
          { label: 'مرتبط بالولاء', value: `${t.linked_pct}%`, prev: pt ? `${pt.linked_pct}%` : null, raw: t.linked_pct, prevRaw: pt?.linked_pct },
          { label: 'أعضاء جدد', value: `${t.new_pct}%`, prev: pt ? `${pt.new_pct}%` : null, raw: t.new_pct, prevRaw: pt?.new_pct },
        ].map((c) => {
          const d = diff(c.raw ?? c.value, c.prevRaw ?? c.prev);
          return (
            <div key={c.label} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 8 }}>
              <div style={{ fontSize: 10, color: '#666', marginBottom: 3 }}>{c.label}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#111' }}>
                {c.value}{c.suffix || ''}
              </div>
              {d !== null && (
                <div style={{ fontSize: 9, color: d > 0 ? '#059669' : '#dc2626', marginTop: 2 }}>
                  {d > 0 ? '▲' : '▼'} {Math.abs(d).toFixed(1)}{c.suffix || ''} مقابل الأسبوع السابق
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Per-barber table */}
      <h3 style={{ fontSize: 13, margin: '8px 0', color: '#111' }}>أداء الحلاقين</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10.5 }}>
        <thead>
          <tr style={{ background: '#1f2937', color: '#fff' }}>
            <th style={{ padding: 5, textAlign: 'right', border: '1px solid #374151' }}>الحلاق</th>
            <th style={{ padding: 5, border: '1px solid #374151' }}>الزيارات</th>
            <th style={{ padding: 5, border: '1px solid #374151' }}>الإيرادات (د.إ)</th>
            <th style={{ padding: 5, border: '1px solid #374151' }}>متوسط الفاتورة</th>
            <th style={{ padding: 5, border: '1px solid #374151' }}>مرتبط %</th>
            <th style={{ padding: 5, border: '1px solid #374151' }}>جدد %</th>
            <th style={{ padding: 5, border: '1px solid #374151' }}>عائد %</th>
            <th style={{ padding: 5, border: '1px solid #374151' }}>غير مشترك %</th>
            <th style={{ padding: 5, border: '1px solid #374151' }}>التقييم</th>
          </tr>
        </thead>
        <tbody>
          {(report.barbers || []).map((b) => (
            <tr key={b.barber_id}>
              <td style={{ padding: 5, border: '1px solid #d1d5db', fontWeight: 700 }}>{b.barber_name}</td>
              <td style={{ padding: 5, border: '1px solid #d1d5db', textAlign: 'center' }}>{b.visits_count}</td>
              <td style={{ padding: 5, border: '1px solid #d1d5db', textAlign: 'left', fontFamily: 'monospace', fontWeight: 700 }}>{b.revenue_total.toFixed(0)}</td>
              <td style={{ padding: 5, border: '1px solid #d1d5db', textAlign: 'left', fontFamily: 'monospace' }}>{b.avg_ticket.toFixed(0)}</td>
              <td style={{ padding: 5, border: '1px solid #d1d5db', textAlign: 'center', color: '#059669', fontWeight: 700 }}>{b.linked_pct}%</td>
              <td style={{ padding: 5, border: '1px solid #d1d5db', textAlign: 'center', color: '#2563eb', fontWeight: 700 }}>{b.new_pct}%</td>
              <td style={{ padding: 5, border: '1px solid #d1d5db', textAlign: 'center', color: '#d4af37', fontWeight: 700 }}>{b.returning_pct}%</td>
              <td style={{ padding: 5, border: '1px solid #d1d5db', textAlign: 'center', color: '#dc2626', fontWeight: 700 }}>{b.unlinked_pct}%</td>
              <td style={{ padding: 5, border: '1px solid #d1d5db', textAlign: 'center', fontWeight: 700 }}>{b.loyalty_score}</td>
            </tr>
          ))}
          {(report.barbers || []).length === 0 && (
            <tr><td colSpan="9" style={{ padding: 16, textAlign: 'center', color: '#888' }}>لا توجد بيانات في هذا الأسبوع</td></tr>
          )}
        </tbody>
      </table>

      <div style={{ marginTop: 18, fontSize: 9.5, color: '#666', borderTop: '1px solid #e5e7eb', paddingTop: 8 }}>
        <div><strong>التقييم</strong> = (نسبة الربط × 0.6) + (نسبة الجدد × 0.4) — مقياس مركّب يقيس الالتزام بالبرنامج وقدرة الحلاق على جذب عملاء جدد.</div>
        <div style={{ marginTop: 4 }}>المقارنة تستمر حتى يكمل برنامج الولاء 4 أشهر.</div>
      </div>
    </div>
  );
});
ReportSheet.displayName = 'ReportSheet';

const WeeklyReports = () => {
  const { user, isAdmin } = useAuth();
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);  // 0 = current preview, 1..N = sealed
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sendingWa, setSendingWa] = useState(false);
  const [activeTab, setActiveTab] = useState('weekly'); // 'weekly' | 'monthly' | 'trend'
  const [monthly, setMonthly] = useState(null);
  const [trend, setTrend] = useState(null);
  const [trendBarber, setTrendBarber] = useState(null);
  const printRef = useRef(null);

  // Rating dialog state
  const [ratingDialog, setRatingDialog] = useState({ open: false, row: null, weekStart: null, weekLabel: null });

  const canClickRow = (barberRow) => {
    if (isAdmin()) return true;
    return user?.barber_id && barberRow?.barber_id === user.barber_id;
  };
  const openRating = (barberRow, weekStart, weekLabel) => {
    if (!canClickRow(barberRow)) {
      toast.error('يمكنك فقط الضغط على تقييمك الشخصي');
      return;
    }
    setRatingDialog({ open: true, row: barberRow, weekStart, weekLabel });
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [cur, hist, mon] = await Promise.all([
        api.get('/weekly-reports/current'),
        api.get('/weekly-reports/history?limit=50'),
        api.get('/weekly-reports/monthly'),
      ]);
      setCurrent(cur.data);
      setHistory(hist.data?.items || []);
      setMonthly(mon.data);
      // Pre-select first barber for trend tab
      const firstBarber =
        (cur.data?.barbers?.[0]) ||
        (hist.data?.items?.[0]?.barbers?.[0]) ||
        null;
      if (firstBarber && !trendBarber) {
        setTrendBarber(firstBarber.barber_id);
      }
    } catch (err) {
      toast.error('فشل تحميل التقارير');
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch trend whenever the selected barber changes
  useEffect(() => {
    if (!trendBarber) return;
    api.get(`/weekly-reports/barber-trend/${trendBarber}?weeks=16`)
      .then((res) => setTrend(res.data))
      .catch(() => setTrend(null));
  }, [trendBarber]);

  const sendWhatsappDigest = async () => {
    if (selectedIdx === 0) {
      toast.error('يلزم اختيار أسبوع مختوم');
      return;
    }
    const target = history[selectedIdx - 1];
    if (!target?.id) return;
    setSendingWa(true);
    try {
      const res = await api.post(`/weekly-reports/${target.id}/send-whatsapp`);
      if (res.data?.ok) {
        toast.success('تم إرسال الملخص للواتساب');
      } else {
        toast.error('فشل الإرسال — تحقق من إعداد واتساب');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل الإرسال');
    } finally {
      setSendingWa(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const generateLastWeek = async () => {
    setGenerating(true);
    try {
      await api.post('/weekly-reports/generate?overwrite=true');
      toast.success('تم توليد تقرير الأسبوع السابق');
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشلت العملية');
    } finally {
      setGenerating(false);
    }
  };

  const selectedReport = useMemo(() => {
    if (selectedIdx === 0) return current;
    return history[selectedIdx - 1] || null;
  }, [selectedIdx, current, history]);

  const previousReport = useMemo(() => {
    // For comparison, use the next chronologically older report
    if (selectedIdx === 0) return history[0] || null; // last sealed week vs current
    return history[selectedIdx] || null;              // sealed week vs prev sealed
  }, [selectedIdx, history]);

  const handleDownloadPDF = useElementToPdf({
    contentRef: printRef,
    documentTitle: `Weekly_Loyalty_Report_${selectedReport?.week_start?.slice(0, 10) || 'preview'}`,
    pageStyle: '@page { size: A4 portrait; margin: 0; }',
  });

  if (loading) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        جاري تحميل التقارير...
      </div>
    );
  }

  const r = selectedReport;
  const t = r?.totals || {};

  return (
    <div className="p-4 sm:p-6 space-y-5" dir="rtl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-primary" />
            التقارير الأسبوعية لبرنامج الولاء
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            تقرير سبت→جمعة يقارن أداء كل حلاق أسبوعياً حتى يكمل البرنامج 4 أشهر
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={fetchAll}
            variant="outline"
            size="sm"
            data-testid="refresh-reports-btn"
          >
            <RefreshCw className="w-4 h-4 me-1" /> تحديث
          </Button>
          <Button
            onClick={generateLastWeek}
            disabled={generating}
            size="sm"
            data-testid="generate-last-week-btn"
          >
            <Calendar className="w-4 h-4 me-1" />
            {generating ? 'جاري...' : 'توليد الأسبوع السابق'}
          </Button>
          <Button
            onClick={sendWhatsappDigest}
            disabled={sendingWa || selectedIdx === 0}
            size="sm"
            variant="outline"
            className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
            title={selectedIdx === 0 ? 'اختر أسبوعاً مختوماً أولاً' : 'إرسال ملخص واتساب للمدير'}
            data-testid="send-whatsapp-btn"
          >
            <Send className="w-4 h-4 me-1" />
            {sendingWa ? 'جاري...' : 'إرسال واتساب'}
          </Button>
          <Button
            onClick={handleDownloadPDF}
            disabled={!r}
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="download-report-pdf-btn"
          >
            <FileText className="w-4 h-4 me-1" /> تنزيل PDF
          </Button>
        </div>
      </div>

      {/* Loyalty performance comparison + overdue unlinked revenues
          (moved here from Dashboard). Each barber sees their own ranking + perf %,
          admin sees the full leaderboard and overdue items. */}
      <UnlinkedRevenuesWidget />

      {/* Tab bar */}
      <div className="flex gap-2 border-b border-white/10 -mb-2">
        {[
          { key: 'weekly', label: 'أسبوعي', icon: Calendar },
          { key: 'monthly', label: 'شهري', icon: Crown },
          { key: 'trend', label: 'الاتجاه التاريخي', icon: LineChartIcon },
        ].map((tb) => {
          const Icon = tb.icon;
          const active = activeTab === tb.key;
          return (
            <button
              key={tb.key}
              onClick={() => setActiveTab(tb.key)}
              data-testid={`tab-${tb.key}`}
              className={`px-4 py-2 -mb-px text-sm font-medium flex items-center gap-2 border-b-2 transition-colors ${
                active
                  ? 'text-primary border-primary'
                  : 'text-muted-foreground border-transparent hover:text-foreground'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tb.label}
            </button>
          );
        })}
      </div>

      {/* === WEEKLY TAB === */}
      {activeTab === 'weekly' && (
      <>
      {/* Week selector */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-sm text-muted-foreground">اختر الأسبوع:</span>
        <Button
          size="sm"
          variant={selectedIdx === 0 ? 'default' : 'outline'}
          onClick={() => setSelectedIdx(0)}
          data-testid="select-current-week"
        >
          📅 الأسبوع الحالي (حي)
        </Button>
        {history.map((h, i) => (
          <Button
            key={h.id}
            size="sm"
            variant={selectedIdx === i + 1 ? 'default' : 'outline'}
            onClick={() => setSelectedIdx(i + 1)}
            data-testid={`select-week-${i}`}
          >
            {h.week_label}
          </Button>
        ))}
      </div>

      {/* KPI cards */}
      {r && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-4 border-amber-500/30">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">إجمالي الزيارات</span>
              <Crown className="w-4 h-4 text-amber-500" />
            </div>
            <div className="text-2xl font-bold">{t.visits_count || 0}</div>
            <div className="text-xs text-muted-foreground mt-1">عميل خلال الأسبوع</div>
          </Card>
          <Card className="p-4 border-emerald-500/30">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">الإيرادات</span>
              <TrendingUp className="w-4 h-4 text-emerald-500" />
            </div>
            <div className="text-2xl font-bold">{Number(t.revenue_total || 0).toFixed(0)} <span className="text-sm">د.إ</span></div>
            <div className="text-xs text-muted-foreground mt-1">متوسط/زيارة: {t.visits_count ? (t.revenue_total / t.visits_count).toFixed(0) : 0} د.إ</div>
          </Card>
          <Card className="p-4 border-blue-500/30">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">أعضاء جدد</span>
              <UserPlus className="w-4 h-4 text-blue-500" />
            </div>
            <div className="text-2xl font-bold text-blue-400">{t.new_pct || 0}%</div>
            <div className="text-xs text-muted-foreground mt-1">{t.new_member_count || 0} عميل التحقوا هذا الأسبوع</div>
          </Card>
          <Card className="p-4 border-yellow-500/30">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">عملاء عائدون</span>
              <Repeat className="w-4 h-4 text-yellow-500" />
            </div>
            <div className="text-2xl font-bold text-yellow-400">{t.returning_pct || 0}%</div>
            <div className="text-xs text-muted-foreground mt-1">{t.returning_count || 0} زيارة من أعضاء قدامى</div>
          </Card>
        </div>
      )}

      {/* Coverage / health row */}
      {r && (
        <Card className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <div className="text-xs text-muted-foreground mb-1">نسبة الربط بالولاء</div>
              <div className="flex items-center gap-2">
                <PctChip value={t.linked_pct} color="#10b981" />
                <span className="text-sm text-muted-foreground">{t.linked_count} زيارة مرتبطة</span>
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">نسبة غير المشتركين</div>
              <div className="flex items-center gap-2">
                <PctChip value={t.unlinked_pct} color="#ef4444" />
                <span className="text-sm text-muted-foreground">{t.unlinked_count} زيارة بدون ربط</span>
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">نقاط الالتزام (مركّبة)</div>
              <ScoreBadge score={(t.linked_pct * 0.6) + (t.new_pct * 0.4)} />
            </div>
          </div>
        </Card>
      )}

      {/* Barbers table */}
      <Card className="p-0 overflow-hidden">
        <div className="p-4 border-b border-white/5">
          <h2 className="font-bold flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            أداء كل حلاق
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60">
              <tr>
                <th className="p-3 text-start">الحلاق</th>
                <th className="p-3 text-center">الزيارات</th>
                <th className="p-3 text-center">الإيرادات</th>
                <th className="p-3 text-center">متوسط/زيارة</th>
                <th className="p-3 text-center">ربط %</th>
                <th className="p-3 text-center">جدد %</th>
                <th className="p-3 text-center">عائد %</th>
                <th className="p-3 text-center">غير مشترك %</th>
                <th className="p-3 text-center">التقييم</th>
              </tr>
            </thead>
            <tbody>
              {(r?.barbers || []).map((b, idx) => (
                <tr
                  key={b.barber_id}
                  className={`${idx % 2 ? 'bg-secondary/20' : ''} ${isAdmin() ? 'hover:bg-primary/5 cursor-pointer' : ''}`}
                  onClick={isAdmin() ? () => openRating(b, r?.week_start, r?.week_label) : undefined}
                  data-testid={`barber-row-${b.barber_id}`}
                >
                  <td className="p-3 font-bold">{b.barber_name}</td>
                  <td className="p-3 text-center">{b.visits_count}</td>
                  <td className="p-3 text-center font-mono">{b.revenue_total.toFixed(0)} د.إ</td>
                  <td className="p-3 text-center font-mono">{b.avg_ticket.toFixed(0)}</td>
                  <td className="p-3 text-center"><PctChip value={b.linked_pct} color="#10b981" /></td>
                  <td className="p-3 text-center"><PctChip value={b.new_pct} color="#2563eb" /></td>
                  <td className="p-3 text-center"><PctChip value={b.returning_pct} color="#d4af37" /></td>
                  <td className="p-3 text-center"><PctChip value={b.unlinked_pct} color="#ef4444" /></td>
                  <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                    <ScoreBadge
                      score={b.loyalty_score}
                      clickable={canClickRow(b)}
                      onClick={() => openRating(b, r?.week_start, r?.week_label)}
                    />
                  </td>
                </tr>
              ))}
              {(r?.barbers || []).length === 0 && (
                <tr>
                  <td colSpan="9" className="p-8 text-center text-muted-foreground">
                    <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-amber-500" />
                    لا توجد بيانات لهذا الأسبوع
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
      </>
      )}

      {/* === MONTHLY TAB === */}
      {activeTab === 'monthly' && (
        <Card className="p-4 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="font-bold text-lg flex items-center gap-2">
                <Crown className="w-5 h-5 text-amber-500" />
                التقرير الشهري — {monthly?.month_label || '—'}
              </h2>
              <p className="text-xs text-muted-foreground">
                ملخص {monthly?.weeks_count || 0} أسابيع مختومة
              </p>
            </div>
          </div>

          {monthly ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="p-3">
                  <div className="text-xs text-muted-foreground">إجمالي العملاء</div>
                  <div className="text-xl font-bold">{monthly.totals?.visits_count || 0}</div>
                </Card>
                <Card className="p-3">
                  <div className="text-xs text-muted-foreground">الإيرادات</div>
                  <div className="text-xl font-bold">{Number(monthly.totals?.revenue_total || 0).toFixed(0)} د.إ</div>
                </Card>
                <Card className="p-3">
                  <div className="text-xs text-muted-foreground">نسبة الربط</div>
                  <div className="text-xl font-bold text-emerald-400">{monthly.totals?.linked_pct || 0}%</div>
                </Card>
                <Card className="p-3">
                  <div className="text-xs text-muted-foreground">نسبة الجدد</div>
                  <div className="text-xl font-bold text-blue-400">{monthly.totals?.new_pct || 0}%</div>
                </Card>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/60">
                    <tr>
                      <th className="p-3 text-start">الحلاق</th>
                      <th className="p-3 text-center">زيارات</th>
                      <th className="p-3 text-center">إيرادات</th>
                      <th className="p-3 text-center">متوسط/زيارة</th>
                      <th className="p-3 text-center">ربط %</th>
                      <th className="p-3 text-center">جدد %</th>
                      <th className="p-3 text-center">عائد %</th>
                      <th className="p-3 text-center">التقييم</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(monthly.barbers || []).map((b, idx) => (
                      <tr
                        key={b.barber_id}
                        className={`${idx % 2 ? 'bg-secondary/20' : ''} ${isAdmin() ? 'hover:bg-primary/5 cursor-pointer' : ''}`}
                        onClick={isAdmin() ? () => openRating(b, null, monthly?.month_label) : undefined}
                      >
                        <td className="p-3 font-bold">{b.barber_name}</td>
                        <td className="p-3 text-center">{b.visits_count}</td>
                        <td className="p-3 text-center font-mono">{b.revenue_total.toFixed(0)} د.إ</td>
                        <td className="p-3 text-center font-mono">{b.avg_ticket.toFixed(0)}</td>
                        <td className="p-3 text-center"><PctChip value={b.linked_pct} color="#10b981" /></td>
                        <td className="p-3 text-center"><PctChip value={b.new_pct} color="#2563eb" /></td>
                        <td className="p-3 text-center"><PctChip value={b.returning_pct} color="#d4af37" /></td>
                        <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                          <ScoreBadge
                            score={b.loyalty_score}
                            clickable={canClickRow(b)}
                            onClick={() => openRating(b, null, monthly?.month_label)}
                          />
                        </td>
                      </tr>
                    ))}
                    {(monthly.barbers || []).length === 0 && (
                      <tr><td colSpan="8" className="p-6 text-center text-muted-foreground">لا توجد بيانات لهذا الشهر</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {monthly.weeks_count > 0 && (
                <div className="text-xs text-muted-foreground border-t border-white/5 pt-3">
                  📌 الأسابيع المُضمّنة: {(monthly.weeks || []).map(w => w.week_label).join(' • ')}
                </div>
              )}
            </>
          ) : (
            <div className="p-8 text-center text-muted-foreground">جاري التحميل...</div>
          )}
        </Card>
      )}

      {/* === TREND TAB === */}
      {activeTab === 'trend' && (
        <Card className="p-4 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="font-bold text-lg flex items-center gap-2">
                <LineChartIcon className="w-5 h-5 text-primary" />
                الاتجاه التاريخي — حتى 4 أشهر
              </h2>
              <p className="text-xs text-muted-foreground">
                تتبّع نسب الربط والجدد والتقييم لكل حلاق عبر الأسابيع
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {(current?.barbers || []).map((b) => (
                <Button
                  key={b.barber_id}
                  size="sm"
                  variant={trendBarber === b.barber_id ? 'default' : 'outline'}
                  onClick={() => setTrendBarber(b.barber_id)}
                  data-testid={`trend-barber-${b.barber_id}`}
                >
                  {b.barber_name}
                </Button>
              ))}
            </div>
          </div>

          {trend && trend.series && trend.series.length > 0 ? (
            <>
              <div className="bg-secondary/30 rounded-lg p-3">
                <h3 className="text-sm font-bold mb-2">نسب الربط / الجدد / التقييم</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={trend.series} margin={{ top: 10, right: 12, left: 0, bottom: 8 }}>
                    <CartesianGrid stroke="#ffffff15" strokeDasharray="3 3" />
                    <XAxis dataKey="week_label" stroke="#94a3b8" fontSize={10} tickFormatter={(v) => v?.slice(5, 10)} />
                    <YAxis stroke="#94a3b8" fontSize={11} />
                    <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8 }} />
                    <Legend />
                    <Line type="monotone" dataKey="linked_pct" name="ربط %" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="new_pct" name="جدد %" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="loyalty_score" name="التقييم" stroke="#d4af37" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-secondary/30 rounded-lg p-3">
                <h3 className="text-sm font-bold mb-2">الزيارات / الإيرادات الأسبوعية</h3>
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={trend.series} margin={{ top: 10, right: 12, left: 0, bottom: 8 }}>
                    <CartesianGrid stroke="#ffffff15" strokeDasharray="3 3" />
                    <XAxis dataKey="week_label" stroke="#94a3b8" fontSize={10} tickFormatter={(v) => v?.slice(5, 10)} />
                    <YAxis yAxisId="left" stroke="#94a3b8" fontSize={11} />
                    <YAxis yAxisId="right" orientation="right" stroke="#94a3b8" fontSize={11} />
                    <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8 }} />
                    <Legend />
                    <Line yAxisId="left" type="monotone" dataKey="visits_count" name="زيارات" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
                    <Line yAxisId="right" type="monotone" dataKey="revenue_total" name="إيراد (د.إ)" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </>
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              لا توجد بيانات تاريخية بعد — ستظهر الرسومات بعد ختم أسبوعين أو أكثر
            </div>
          )}
        </Card>
      )}

      {/* Off-screen printable sheet */}
      <div style={{ position: 'fixed', left: '-10000px', top: 0, pointerEvents: 'none' }} aria-hidden="true">
        <ReportSheet ref={printRef} report={r} prevReport={previousReport} />
      </div>

      {/* Barber rating dialog */}
      <BarberRatingDialog
        open={ratingDialog.open}
        barberRow={ratingDialog.row}
        weekStart={ratingDialog.weekStart}
        weekLabel={ratingDialog.weekLabel}
        canEdit={ratingDialog.row && canClickRow(ratingDialog.row)}
        currentUserRole={user?.role}
        onClose={() => setRatingDialog({ open: false, row: null, weekStart: null, weekLabel: null })}
      />
    </div>
  );
};

export default WeeklyReports;
